import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Bot, SendHorizonal } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ButtonMain from "@/components/buttons/button_main";
import InputField from "@/components/forms/input_field";
import TextareaField from "@/components/forms/textarea_field";

const RULES = [
  { match: /price|pricing|cost|fee|pay|plan/i, reply: "Renters get 20 free swipes a day, and matching and chatting are always free. Renter Plus at $9.99 per month adds unlimited swiping and shows you who liked you. Listers get their first listing free, then $9 per listing, and Pro at $29 per month shows everyone who liked their homes. See the Pricing page for everything." },
  { match: /how|work|start|swipe/i, reply: "SwipRent works like this: set your filters, swipe right on homes you love, and when a lister likes you back it is a match. Then you chat, book a viewing, and can even sign the lease right in the app." },
  { match: /screen|background|credit|reference|trust/i, reply: "Applicant screening with credit and background checks is coming soon. Every account is already identity verified with an ID and selfie check, and listers can request references right in the chat." },
  { match: /lease|contract|sign|document/i, reply: "When you and a lister agree on terms, SwipRent generates the correct provincial lease form and both sides e sign it inside the app. No printing, no scanning." },
  { match: /contact|human|support|help|email/i, reply: "You can reach our team any time at info@swiprent.com, or tap Talk to a human below and we will get back to you within one business day." },
];

const GREETING = { id: "g", bot: true, text: "Hi, I am SwipBot. Ask me about pricing, how SwipRent works, screening, or leases." };

// THE one per-route mobile offset map for the FAB and its chat panel. First
// match wins; every entry is a complete literal class string so Tailwind can
// see it. Desktop (md+) always uses bottom-6 / bottom-24 via the md classes.
const FAB_OFFSETS = [
  {
    // Listing detail: the sticky "Like this place" price bar sits on top of
    // the bottom nav, so the FAB floats above BOTH (nav 3.75rem + bar
    // ~4.75rem + 12px gap). The panel opens above the raised FAB.
    match: (p) => p.startsWith("/listing/"),
    fab: "bottom-[calc(8.5rem+12px+env(safe-area-inset-bottom))]",
    panel: "bottom-[calc(13.5rem+env(safe-area-inset-bottom))]",
  },
  {
    // Landing: the swipe action row is truly centered under the hero card
    // (no FAB clearance padding on the row), so the FAB floats above the
    // whole row zone (nav 4.5rem + row ~4rem + 12px gap).
    match: (p) => p === "/",
    fab: "bottom-[calc(8.5rem+12px+env(safe-area-inset-bottom))]",
    panel: "bottom-[calc(13.5rem+env(safe-area-inset-bottom))]",
  },
  {
    // Default: 12px above the bottom tab bar.
    match: () => true,
    fab: "bottom-[calc(4.5rem+12px+env(safe-area-inset-bottom))]",
    panel: "bottom-[calc(9rem+env(safe-area-inset-bottom))]",
  },
];

export default function BotWidget() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  // The mobile map poster broadcasts its open state so the FAB can step
  // aside while the poster covers the lower half of the map.
  const [mapPosterOpen, setMapPosterOpen] = useState(false);
  useEffect(() => {
    function onPoster(e) {
      setMapPosterOpen(!!e.detail?.open);
    }
    window.addEventListener("swiprent:map-poster", onPoster);
    return () => window.removeEventListener("swiprent:map-poster", onPoster);
  }, []);
  const [mode, setMode] = useState("chat");
  const [messages, setMessages] = useState([GREETING]);
  const [text, setText] = useState("");
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, open]);

  function localAnswer(q) {
    const rule = RULES.find((r) => r.match.test(q));
    return rule ? rule.reply : "Good question. I cover pricing, how SwipRent works, screening and leases. For anything else, tap Talk to a human and our team will help via info@swiprent.com.";
  }

  function send(e) {
    e.preventDefault();
    const q = text.trim();
    if (!q) return;
    setText("");
    setMessages((m) => [...m, { id: `u${Date.now()}`, bot: false, text: q }]);
    api
      .post("/bot/message", { text: q })
      .then((res) => setMessages((m) => [...m, { id: `b${Date.now()}`, bot: true, text: res.data?.reply || localAnswer(q) }]))
      .catch(() => setMessages((m) => [...m, { id: `b${Date.now()}`, bot: true, text: localAnswer(q) }]));
  }

  function escalate(e) {
    e.preventDefault();
    api.post("/bot/escalate", form).catch(() => {});
    toast.success("Message sent to info@swiprent.com. We will reply within one business day.");
    setForm({ name: "", email: "", message: "" });
    setMode("chat");
    setMessages((m) => [...m, { id: `b${Date.now()}`, bot: true, text: "Thanks, your message is on its way to our team at info@swiprent.com." }]);
  }

  // No chat bubble inside chat: on /chat/* the FAB is redundant and covers
  // the send button, so the widget stays off those routes entirely.
  // Also off /swipe: the full-bleed deck is a clean Tinder-style surface and
  // the bubble read as a messages button sitting on the card (owner call).
  if (pathname.startsWith("/chat") || pathname === "/swipe") return null;

  const offsets = FAB_OFFSETS.find((o) => o.match(pathname)) || FAB_OFFSETS[FAB_OFFSETS.length - 1];
  // Mobile map with the poster expanded: the FAB hides entirely so it never
  // overlaps the poster or its action row. Desktop map keeps the FAB.
  const hideFabMobile = pathname === "/map" && mapPosterOpen;

  return (
    <>
      {/* FAB rhythm matches the swipe action row: 56px round button, 16px
          right inset, and on mobile it floats 12px above whatever fixed bar
          the current route stacks at the bottom (see FAB_OFFSETS). Pages with
          bottom-right controls reserve this band with the shared
          .pb-fab-clear utility (index.css) so nothing sits under the bubble. */}
      <button
        type="button"
        aria-label={open ? "Close SwipBot" : "Chat with SwipBot"}
        onClick={() => setOpen((v) => !v)}
        className={`fixed right-4 z-[45] h-14 w-14 items-center justify-center rounded-full bg-brand-primary text-white shadow-ambient transition hover:bg-brand-primary-hover active:scale-95 md:right-6 ${offsets.fab} md:bottom-6 ${hideFabMobile ? "hidden lg:flex" : "flex"}`}
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className={`fixed right-3 z-[45] flex h-[440px] max-h-[70dvh] w-[calc(100vw-24px)] max-w-sm flex-col overflow-hidden rounded-3xl border border-night-line bg-night-surface shadow-2xl ${offsets.panel} md:bottom-24 md:right-6 md:max-h-none md:w-[calc(100vw-2rem)]`}
          >
            <div className="flex items-center gap-2.5 border-b border-night-line bg-night px-4 py-3.5 text-night-text">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-primary text-white">
                <Bot size={17} />
              </span>
              <div>
                <p className="text-sm font-extrabold">SwipBot</p>
                <p className="text-[11px] font-semibold text-night-sub">Usually replies instantly</p>
              </div>
            </div>

            {mode === "chat" ? (
              <>
                <div className="flex-1 space-y-2.5 overflow-y-auto bg-night p-3.5">
                  {messages.map((m) => (
                    <div key={m.id} className={`flex ${m.bot ? "justify-start" : "justify-end"}`}>
                      <p className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-[13px] font-medium leading-relaxed shadow-sm ${m.bot ? "rounded-bl-md bg-night-surface text-night-text" : "rounded-br-md bg-night-tint text-night-text"}`}>
                        {m.text}
                      </p>
                    </div>
                  ))}
                  <div ref={bottomRef} />
                </div>
                <div className="border-t border-night-line bg-night-surface p-2.5">
                  <form onSubmit={send} className="flex items-center gap-2">
                    <input
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Ask SwipBot anything"
                      className="flex-1 rounded-full border border-night-line bg-night px-4 py-2 text-sm text-night-text placeholder:text-night-sub outline-none transition focus:border-brand-primary"
                    />
                    <button type="submit" aria-label="Send" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-primary text-white transition hover:bg-brand-primary-hover md:h-9 md:w-9">
                      <SendHorizonal size={15} />
                    </button>
                  </form>
                  <button type="button" onClick={() => setMode("human")} className="mt-1.5 w-full text-center text-xs font-bold text-night-sub transition hover:text-brand-primary">
                    Talk to a human
                  </button>
                </div>
              </>
            ) : (
              <form onSubmit={escalate} className="flex-1 space-y-3 overflow-y-auto p-4">
                <p className="text-xs font-semibold text-night-sub">Your message goes straight to our team at info@swiprent.com.</p>
                <InputField label="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <InputField label="Email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <TextareaField label="Message" rows={3} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                <ButtonMain type="submit" full>Send message</ButtonMain>
                <button type="button" onClick={() => setMode("chat")} className="w-full text-center text-xs font-bold text-night-sub transition hover:text-brand-primary">
                  Back to SwipBot
                </button>
              </form>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
